import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t.contact.validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.successMessage);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t.contact.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold/60 mb-8" data-testid="contact-label">
            {t.contact.label}
          </p>
          <h2 className="text-4xl md:text-6xl tracking-tight leading-none text-zinc-100 mb-16" data-testid="contact-title">
            {t.contact.title}
          </h2>

          <div className="max-w-3xl">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.namePlaceholder}
                  data-testid="contact-name-input"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-gold px-0 py-4 text-white placeholder:text-zinc-700 transition-colors focus:ring-0 focus:outline-none"
                  disabled={isSubmitting}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.emailPlaceholder}
                  data-testid="contact-email-input"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-gold px-0 py-4 text-white placeholder:text-zinc-700 transition-colors focus:ring-0 focus:outline-none"
                  disabled={isSubmitting}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.messagePlaceholder}
                  rows={6}
                  data-testid="contact-message-input"
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-gold px-0 py-4 text-white placeholder:text-zinc-700 transition-colors focus:ring-0 focus:outline-none resize-none"
                  disabled={isSubmitting}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={isSubmitting}
                  className="bg-gold text-black hover:bg-white transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t.contact.submitting : t.contact.submitButton}
                </button>
              </motion.div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-32 pt-12 border-t border-zinc-900">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-sm text-zinc-600">
            © {new Date().getFullYear()} Neural Brain Team. {t.footer.copyright}
          </p>
          <p className="text-xs text-zinc-700 uppercase tracking-[0.2em]">
            {t.footer.tagline}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
